import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { IDataMinerIncentiveItem } from "../../../services/zeos-api/contracts/DataMiner";
import { DashboardFooter } from "../layout/DashboardFooter";
import { DashboardHeader } from "../layout/DashboardHeader";
import { buildDataMinerQuery, parseDataMinerQuery } from "./dataMinerQuery";

export const DataMinerCategory = () => {
  const api = useContext(ZeosApiContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { category } = useParams();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<IDataMinerIncentiveItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const queryFilters = useMemo(
    () => parseDataMinerQuery(location.search),
    [location.search]
  );

  const categoryBySlug: Record<string, { label: string; filter: string }> = {
    "ev-infrastructure": { label: "EV Infrastructure", filter: "EV Infrastructure" },
    "energy-storage": { label: "Energy Storage (BESS)", filter: "Energy Storage (BESS)" },
    "electrification-hvac": {
      label: "Electrification / HVAC",
      filter: "Building",
    },
    "energy-efficiency": {
      label: "Energy Efficiency & Controls",
      filter: "Building",
    },
    "renewable-generation": {
      label: "Renewable Generation",
      filter: "Power Generation",
    },
    "grid-resilience": {
      label: "Grid / Resilience Programs",
      filter: "Grid / Resilience Programs",
    },
  };

  const titleCaseSlug = (value: string) =>
    value
      .split("-")
      .map((word) => (word.length <= 2 ? word.toUpperCase() : `${word[0].toUpperCase()}${word.slice(1)}`))
      .join(" ");

  const resolvedCategory = useMemo(() => {
    const state = (location.state as {
      category?: string;
      location?: string;
      geographyType?: string;
      utilityTerritory?: string;
      filters?: {
        categories?: string[];
        areas?: string[];
        types?: string[];
      };
    } | null) ?? { category: undefined, filters: undefined };

    if (state.category) {
      return { label: state.category, filter: state.category };
    }

    if (!category) {
      return { label: "All Incentives", filter: "" };
    }

    const mapped = categoryBySlug[category.toLowerCase()];
    if (mapped) return mapped;

    const fallback = titleCaseSlug(category);
    return { label: fallback, filter: fallback };
  }, [category, location.state]);

  const locationMetadata = useMemo(() => {
    const state = (location.state as {
      location?: string;
      geographyType?: string;
      utilityTerritory?: string;
      isDac?: boolean;
    } | null) ?? { location: undefined, geographyType: undefined, utilityTerritory: undefined };
    return {
      location: state.location ?? queryFilters.location,
      geographyType: state.geographyType ?? queryFilters.geographyType,
      utilityTerritory: state.utilityTerritory ?? queryFilters.utilityTerritory,
      isDac: state.isDac ?? queryFilters.isDac,
    };
  }, [location.state, queryFilters.geographyType, queryFilters.location, queryFilters.utilityTerritory, queryFilters.isDac]);

  const resolvedFilters = useMemo(() => {
    const state = (location.state as {
      filters?: {
        categories?: string[];
        areas?: string[];
        types?: string[];
        isDac?: boolean;
      };
      location?: string;
    } | null) ?? { filters: undefined };

    if (state.filters) {
      return {
        categories: state.filters.categories ?? [],
        areas: state.filters.areas ?? (state.location ? [state.location] : []),
        types: state.filters.types ?? [],
        isDac: state.filters.isDac,
      };
    }

    if (
      queryFilters.categories ||
      queryFilters.areas ||
      queryFilters.types ||
      queryFilters.location ||
      queryFilters.isDac !== undefined
    ) {
      return {
        categories: queryFilters.categories ?? (resolvedCategory.filter ? [resolvedCategory.filter] : []),
        areas: queryFilters.areas ?? (queryFilters.location ? [queryFilters.location] : []),
        types: queryFilters.types ?? [],
        isDac: queryFilters.isDac,
      };
    }

    return {
      categories: resolvedCategory.filter ? [resolvedCategory.filter] : [],
      areas: [],
      types: [],
      isDac: undefined,
    };
  }, [location.state, queryFilters, resolvedCategory.filter]);

  useEffect(() => {
    const loadAsync = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.DataMiner.matchIncentives({
          categories: resolvedFilters.categories,
          areas: resolvedFilters.areas,
          types: resolvedFilters.types,
          isDac: resolvedFilters.isDac,
        });
        if (response.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(response.message || "Failed to load incentives.");
        }
        setRows(response.data || []);
      } catch (err: any) {
        setError(err?.message || "Failed to load incentives.");
      } finally {
        setLoading(false);
      }
    };
    loadAsync();
  }, [api.DataMiner, category, resolvedFilters]);

  return (
    <div className="flex flex-col min-h-screen">
      <DashboardHeader />
      <main className="bg-zeos-white-md p-4 flex-grow">
        <div className="grid grid-cols-12 gap-6 font-sans">
          <div className="col-span-12 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-black">
                {resolvedCategory.label}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Incentives aligned to this Data Miner category
                {locationMetadata.location ? ` for ${locationMetadata.location}` : ""}.
              </p>
              {locationMetadata.location && (
                <p className="text-xs text-gray-500 mt-1">
                  {locationMetadata.geographyType || "Geography"} •{" "}
                  {locationMetadata.utilityTerritory || "Utility territory"}
                  {locationMetadata.isDac !== undefined && (
                    <>
                      {" "}
                      • DAC: {locationMetadata.isDac ? "Yes" : "No"}
                    </>
                  )}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate("/incentives/data-miner")}
              className="text-sm text-zeos-orange-dk hover:underline"
            >
              Back to Data Miner
            </button>
          </div>

          <div className="col-span-12 bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-700">
            <div className="font-semibold mb-2">Test Links</div>
            <div className="flex flex-wrap gap-2">
              <a
                className="text-zeos-orange-dk hover:underline"
                href={`/incentives/data-miner/energy-storage${buildDataMinerQuery({
                  location: "Los Angeles County, CA",
                  geographyType: "County",
                  utilityTerritory: "LADWP (auto)",
                  categories: ["Energy Storage (BESS)"],
                  isDac: true,
                })}`}
              >
                LA County Storage (DAC)
              </a>
              <a
                className="text-zeos-orange-dk hover:underline"
                href={`/incentives/data-miner/energy-storage${buildDataMinerQuery({
                  location: "Los Angeles County, CA",
                  geographyType: "County",
                  utilityTerritory: "LADWP (auto)",
                  categories: ["Energy Storage (BESS)"],
                  isDac: false,
                })}`}
              >
                LA County Storage (Non-DAC)
              </a>
              <a
                className="text-zeos-orange-dk hover:underline"
                href={`/incentives/data-miner/energy-storage${buildDataMinerQuery({
                  location: "New York County, NY",
                  geographyType: "County",
                  utilityTerritory: "ConEd (auto)",
                  categories: ["Energy Storage (BESS)"],
                })}`}
              >
                NY County Storage
              </a>
            </div>
          </div>

          <div className="col-span-12 bg-white border border-gray-200 rounded p-4">
            {loading && <p className="text-sm text-gray-600">Loading…</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}
            {!loading && !error && rows.length === 0 && (
              <p className="text-sm text-gray-600">No incentives found.</p>
            )}
            {!loading && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-100 text-left">
                    <tr>
                      <th className="px-3 py-2 border-b">Program</th>
                      <th className="px-3 py-2 border-b">Incentive</th>
                      <th className="px-3 py-2 border-b">State</th>
                      <th className="px-3 py-2 border-b">Area</th>
                      <th className="px-3 py-2 border-b">Type</th>
                      <th className="px-3 py-2 border-b">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.incentiveProgramId}-${row.incentiveId || 0}`} className="border-t">
                        <td className="px-3 py-2">{row.programName || "-"}</td>
                        <td className="px-3 py-2">{row.incentiveName || "-"}</td>
                        <td className="px-3 py-2">{row.stateCode || "-"}</td>
                        <td className="px-3 py-2">{row.area || "-"}</td>
                        <td className="px-3 py-2">{row.type || "-"}</td>
                        <td className="px-3 py-2">{row.programStatus || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};
